import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { Employee, QuoteDetail, TechnicianGroup } from '@quorum/models/xs-resource';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { FadeInOut } from '@quorum/sha-animations';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { ToyotaDeclinedReasonDialogComponent } from '../toyota-declined-reason-dialog/toyota-declined-reason-dialog.component';

@Component({
  selector: 'svc-toyota-schedule',
  templateUrl: './toyota-schedule.component.html',
  styleUrls: ['./toyota-schedule.component.scss'],
  animations: [
    FadeInOut,
    trigger('flyOutIn', [
      state(
        'shrink',
        style({
          display: 'none',
          flex: 0,
          transform: 'translateX(-160%)',
        })
      ),
      state(
        'full',
        style({
          display: 'flex',
        })
      ),
      transition('full => shrink', animate('138ms ease-in')),
      transition('shrink => full', animate('138ms ease-out')),
    ]),
    trigger('flyInOut', [
      state(
        'full',
        style({
          'box-sizing': 'border-box',
          flex: '1 1 100%',
          display: 'flex',
          width: '28%',
          'max-width': '28%',
        })
      ),
      state(
        'shrink',
        style({
          'box-sizing': 'border-box',
          flex: '1 1 100%',
          display: 'flex',
          width: '100%',
          'max-width': '100%',
        })
      ),
      transition('full => shrink', animate('138ms ease-in')),
      transition('shrink => full', animate('138ms ease-out')),
    ]),
  ],
})
export class ToyotaScheduleComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  scheduleData$: any;
  alertsUi: any = { collapsed: false };
  onStarUi: any = { collapsed: true };
  claimsHistoryUi: any = { collapsed: true };
  previousUi: any = { collapsed: true };
  previousFactoryRecommendedServicesUi: any = { collapsed: false };
  previousDealerRecommendedServicesUi: any = { collapsed: false };
  currentUi: any = { collapsed: false };
  currentFactoryRecommendedServicesUi: any = { collapsed: false };
  currentDealerRecommendedServicesUi: any = { collapsed: false };
  futureUi: any = { collapsed: true };
  futureFactoryRecommendedServicesUi: any = { collapsed: false };
  futureDealerRecommendedServicesUi: any = { collapsed: false };
  dank = 'full';
  approvedQuoteLines: QuoteDetail[] = [];
  declinedQuoteLines: QuoteDetail[] = [];
  quoteLines$: Observable<QuoteDetail[]>;
  declinedReasons: any[] = [
    { id: 1, reason: 'Will Complete At Next Visit' },
    { id: 2, reason: 'Customer Declined' },
    { id: 3, reason: 'Performed Elsewhere' },
    { id: 4, reason: 'Service Not Required' },
    { id: 5, reason: 'No Time To Perform Campaign Today' },
    { id: 6, reason: 'Other' },
  ];
  previousFactoryRecommendedTechSelection: number[] = [];
  previousDealerRecommendedTechSelection: number[] = [];
  currentFactoryRecommendedTechSelection: number[] = [];
  currentDealerRecommendedTechSelection: number[] = [];
  futureFactoryRecommendedTechSelection: number[] = [];
  futureDealerRecommendedTechSelection: number[] = [];
  authenticatedEmployee$: Observable<Employee>;

  @Input() collapsed: boolean;
  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    public serviceLaneFacade: ServiceLaneFacade,
    public serviceLaneDropdownsFacade: ServiceLaneDropdownsFacade,
    private authenticationStateService: AuthenticationStateService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.serviceLaneFacade.approvedQuoteLines$.pipe(takeUntil(this.destroy$)).subscribe((quoteLines) => {
      this.approvedQuoteLines = quoteLines;
    });

    this.serviceLaneFacade.declinedQuoteLines$.pipe(takeUntil(this.destroy$)).subscribe((quoteLines) => {
      this.declinedQuoteLines = quoteLines;
    });

    this.quoteLines$ = this.serviceLaneFacade.quoteLines$;

    this.authenticatedEmployee$ = this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(filter((authEmployee) => authEmployee != null));

    this.scheduleData$ = combineLatest([
      this.route.queryParams,
      this.serviceLaneFacade.vehicle$.pipe(filter((vehicle) => vehicle !== null)),
    ]).pipe(
      map(([params, vehicle]) => {
        return { params, vehicle };
      }),
      switchMap((data) => {
        if (data?.vehicle?.deliveryDate !== null) {
          return this.apiService
            .get<any>('v/1/fixed-operations/appointments/' + data.params.appointmentId + '/toyota-menu', {
              params: { embed: 'vehicle' },
            })
            .pipe(
              mergeMap((toyotaMenuData) => {
                toyotaMenuData.previous.factoryRecommended.forEach((item: any, index: number) => {
                  this.previousFactoryRecommendedTechSelection[index] = item.serviceGroup.technicianGroupId
                    ? item.serviceGroup.technicianGroupId
                    : item.serviceGroup.technicianId;
                });

                toyotaMenuData.previous.dealerRecommended.forEach((item: any, index: number) => {
                  this.previousDealerRecommendedTechSelection[index] = item.serviceGroup.technicianGroupId
                    ? item.serviceGroup.technicianGroupId
                    : item.serviceGroup.technicianId;
                });

                toyotaMenuData.current.factoryRecommended.forEach((item: any, index: number) => {
                  this.currentFactoryRecommendedTechSelection[index] = item.serviceGroup.technicianGroupId
                    ? item.serviceGroup.technicianGroupId
                    : item.serviceGroup.technicianId;
                });

                toyotaMenuData.current.dealerRecommended.forEach((item: any, index: number) => {
                  this.currentDealerRecommendedTechSelection[index] = item.serviceGroup.technicianGroupId
                    ? item.serviceGroup.technicianGroupId
                    : item.serviceGroup.technicianId;
                });

                toyotaMenuData.future.factoryRecommended.forEach((item: any, index: number) => {
                  this.futureFactoryRecommendedTechSelection[index] = item.serviceGroup.technicianGroupId
                    ? item.serviceGroup.technicianGroupId
                    : item.serviceGroup.technicianId;
                });

                toyotaMenuData.future.dealerRecommended.forEach((item: any, index: number) => {
                  this.futureDealerRecommendedTechSelection[index] = item.serviceGroup.technicianGroupId
                    ? item.serviceGroup.technicianGroupId
                    : item.serviceGroup.technicianId;
                });

                return of({ toyotaMenuData: toyotaMenuData, vehicle: data.vehicle });
              }),
              catchError(() => of({ vehicle: data.vehicle, loadFailed: true }))
            );
        } else {
          return of({ vehicle: data.vehicle });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.collapsed.currentValue) {
      this.alertsUi.collapsed = changes.collapsed.firstChange ? false : true;
      this.onStarUi.collapsed = true;
      this.claimsHistoryUi.collapsed = true;
    } else {
      this.alertsUi.collapsed = false;
      this.onStarUi.collapsed = false;
      this.claimsHistoryUi.collapsed = false;
    }
  }

  toggleCollapse(group: any[], groupUiObject: any) {
    groupUiObject.collapsed = !groupUiObject.collapsed;
    group.forEach((group) => (group.collapsed = groupUiObject.collapsed));
  }

  acceptService(menuItem: any, selected: number, technicians: Employee[], techGroups: TechnicianGroup[]) {
    if (technicians.findIndex((t) => t.associateId == selected.toString()) > -1) {
      const tech: Employee = technicians.find((t) => t.associateId == selected.toString());
      menuItem.serviceGroup.technicianId = tech.associateId;
      menuItem.serviceGroup.technicianGroupName = `${tech?.embedded?.associate?.firstName} ${tech?.embedded?.associate?.lastName} `;
      menuItem.serviceGroup.technicianGroupId = null;
    } else {
      const group: TechnicianGroup = techGroups.find((g) => g.id == selected);
      menuItem.serviceGroup.technicianId = null;
      menuItem.serviceGroup.technicianGroupName = group.description;
      menuItem.serviceGroup.technicianGroupId = group.id;
    }

    this.serviceLaneFacade.acceptQuoteFromToyotaMenu$.next(menuItem);
  }

  checkServiceGroupStatus(status: string, serviceGroup: any) {
    let statusArray: any = [];

    switch (status) {
      case 'pending-accepted':
        statusArray = [];
        break;
      case 'pending-declined':
        statusArray = [];
        break;
      case 'saved-accepted':
        statusArray = this.approvedQuoteLines;
        break;
      case 'saved-declined':
        statusArray = this.declinedQuoteLines;
        break;
    }

    if (statusArray.find((e: any) => e.serviceGroupId === serviceGroup.id)) {
      return true;
    } else {
      return false;
    }
  }

  removeDeclinedService(menuItem: any, quoteLines: any) {
    const selectedQuoteLine = quoteLines.find((ql: QuoteDetail) => ql.serviceGroupId === menuItem.serviceGroup.id);

    this.serviceLaneFacade.declineQuoteFromToyotaMenu$.next(null);
    this.serviceLaneFacade.removeDeclinedQuoteLine(selectedQuoteLine);
    this.serviceLaneFacade.deleteQuoteLineFromServer(selectedQuoteLine);
  }

  selectDeclinedReason(menuItem: any, selected: number, technicians: Employee[], techGroups: TechnicianGroup[]) {
    const dialogRef = this.dialog.open(ToyotaDeclinedReasonDialogComponent, {
      width: '400px',
      data: { declinedReasons: this.declinedReasons },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result.cancelled) {
        if (technicians.findIndex((t) => t.associateId == selected.toString()) > -1) {
          const tech: Employee = technicians.find((t) => t.associateId == selected.toString());
          menuItem.serviceGroup.technicianId = tech.associateId;
          menuItem.serviceGroup.technicianGroupName = `${tech?.embedded?.associate?.firstName} ${tech?.embedded?.associate?.lastName} `;
          menuItem.serviceGroup.technicianGroupId = null;
        } else {
          const group: TechnicianGroup = techGroups.find((g) => g.id == selected);
          menuItem.serviceGroup.technicianId = null;
          menuItem.serviceGroup.technicianGroupName = group.description;
          menuItem.serviceGroup.technicianGroupId = group.id;
        }

        this.serviceLaneFacade.declineQuoteFromToyotaMenu$.next({
          menuItem: menuItem,
          declinedReason: result.selectedDeclinedReason,
        });
      }
    });
  }

  removeAcceptedService(menuItem: any, quoteLines: any) {
    const selectedQuoteLine = quoteLines.find((ql: QuoteDetail) => ql.serviceGroupId === menuItem.serviceGroup.id);

    this.serviceLaneFacade.acceptQuoteFromToyotaMenu$.next(null);
    this.serviceLaneFacade.removeApprovedQuoteLine(selectedQuoteLine);
    this.serviceLaneFacade.deleteQuoteLineFromServer(selectedQuoteLine);
  }
}
